/***
*
*   ALERTS PANEL
*   Shows system alerts: over-quota, inactive orgs, expiring trials
*
**********/

import { Card, Badge, Button, Icon } from 'components/lib';
import Style from './dashboard.tailwind.js';

export function AlertsPanel(props) {
  const { data = {}, loading, onViewOrg, onSeeMore, maxItems = 6 } = props;
  const { alerts = [], summary = {} } = data;

  const getAlertConfig = (type, severity) => {
    switch (type) {
      case 'storage':
        return { 
          icon: 'alert-triangle', 
          color: 'red', 
          label: 'Over Quota',
          bgClass: Style.alertItemRed
        };
      case 'storage_warning':
        return { 
          icon: 'alert-triangle', 
          color: 'orange', 
          label: 'Storage Warning',
          bgClass: Style.alertItemOrange
        };
      case 'inactive':
        return { 
          icon: 'clock', 
          color: 'orange', 
          label: 'Inactive',
          bgClass: Style.alertItemOrange
        };
      case 'trial_expiring':
        return { 
          icon: 'calendar', 
          color: 'blue', 
          label: 'Trial Expiring',
          bgClass: Style.alertItemBlue
        };
      case 'trial_expired':
        return { 
          icon: 'calendar', 
          color: 'red', 
          label: 'Trial Expired',
          bgClass: Style.alertItemRed
        };
      case 'employee_limit':
        return { 
          icon: 'users', 
          color: 'orange', 
          label: 'Employee Limit',
          bgClass: Style.alertItemOrange
        };
      case 'job_opening_limit':
        return { 
          icon: 'briefcase', 
          color: 'orange', 
          label: 'Job Opening Limit',
          bgClass: Style.alertItemOrange
        };
      case 'payment_failed':
        return { 
          icon: 'credit-card', 
          color: 'red', 
          label: 'Payment Failed',
          bgClass: Style.alertItemRed
        };
      default:
        return { 
          icon: 'info', 
          color: severity === 'high' ? 'red' : severity === 'medium' ? 'orange' : 'gray', 
          label: type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
          bgClass: severity === 'high' ? Style.alertItemRed : Style.alertItemOrange
        };
    }
  };

  const formatAlertDetail = (alert) => {
    const { type, details } = alert;
    
    if (type === 'storage' && details) {
      return `${details.used}MB / ${details.allocated}MB (+${details.overage}MB over)`;
    }
    if (type === 'trial_expired' && details) {
      return `Expired ${Math.abs(details.days_remaining)} days ago`;
    }
    if (type === 'trial_expiring' && details) {
      return `${details.days_remaining} days remaining`;
    }
    if (type === 'inactive' && details?.last_active) {
      return `Last active: ${new Date(details.last_active).toLocaleDateString()}`;
    }
    if (type === 'employee_limit' && details) {
      return `${details.current}/${details.limit} employees`;
    }
    if (type === 'job_opening_limit' && details) {
      return `${details.current}/${details.limit} openings`;
    }
    
    return null;
  };

  const totalAlerts = summary.total || alerts.length;
  const displayedAlerts = alerts.slice(0, maxItems);

  return (
    <Card 
      title={
        <span className={Style.alertTitle}>
          Alerts 
          {totalAlerts > 0 && (
            <Badge text={totalAlerts.toString()} color="red" />
          )}
        </span>
      } 
      loading={loading}
    >
      <div className={Style.alertsList}>
        {displayedAlerts.length > 0 ? (
          <>
            {displayedAlerts.map((alert, index) => {
              const config = getAlertConfig(alert.type, alert.severity);
              const detail = formatAlertDetail(alert);
              
              return (
                <div key={index} className={`${Style.alertItem} ${config.bgClass}`}>
                  <div className={Style.alertIcon}>
                    <Icon 
                      image={config.icon} 
                      color={config.color}
                      size={18}
                    />
                  </div>
                  <div className={Style.alertContent}>
                    <div className={Style.alertName}>
                      {alert.message}
                    </div>
                    <div className={Style.alertMeta}>
                      <Badge text={config.label} color={config.color} />
                      <Badge 
                        text={alert.severity} 
                        color={alert.severity === 'high' ? 'red' : alert.severity === 'medium' ? 'orange' : 'gray'} 
                      />
                      {detail && (
                        <span className={Style.alertDetail}>{detail}</span>
                      )}
                    </div>
                  </div>
                  {onViewOrg && alert.workspace_id && (
                    <Button 
                      icon="eye"
                      size="small"
                      color="transparent"
                      action={() => onViewOrg(alert.workspace_id)}
                    />
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <div className={Style.noAlerts}>
            <Icon image="check-circle" color="green" size={24} />
            <span>No alerts - Everything looks good!</span>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {summary.total > 0 && (
        <div className={Style.alertSummary}>
          <div className={Style.summaryItem}>
            <span className={Style.summaryCount + ' ' + Style.summaryHigh}>{summary.high || 0}</span>
            <span className={Style.summaryLabel}>High</span>
          </div>
          <div className={Style.summaryItem}>
            <span className={Style.summaryCount + ' ' + Style.summaryMedium}>{summary.medium || 0}</span>
            <span className={Style.summaryLabel}>Medium</span>
          </div>
          <div className={Style.summaryItem}>
            <span className={Style.summaryCount + ' ' + Style.summaryLow}>{summary.low || 0}</span>
            <span className={Style.summaryLabel}>Low</span>
          </div>
        </div>
      )}

      {/* See More Button */}
      {totalAlerts > maxItems && onSeeMore && (
        <div className={Style.seeMoreContainer}>
          <Button
            text={`See All ${totalAlerts} Alerts`}
            color="secondary"
            size="small"
            action={onSeeMore}
          />
        </div>
      )}
    </Card>
  );
}